import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/Badge';
import { Calendar, Clock, Play, Pause, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';

// Types for our dummy data
interface ScanType {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
}

interface ScanStatus {
  id: string;
  scanType: string;
  status: 'running' | 'monitoring' | 'on-hold' | 'paused' | 'scheduled' | 'completed' | 'failed';
  progress: number;
  startTime: string;
  endTime?: string;
  findings?: number;
}

// Dummy data for scan types
const scanTypes: ScanType[] = [
  { 
    id: 'code-leaks', 
    name: 'Code Leaks', 
    description: 'Scan for source code leaks in public repositories and paste sites',
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-code"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>
  },
  { 
    id: 'third-party-risks', 
    name: '3rd Party Risks', 
    description: 'Assess security risks from third-party vendors and dependencies',
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-boxes"><path d="M7 5v11a3 3 0 0 0 3 3h4a3 3 0 0 0 3-3V5"></path><path d="M4 12a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1Z"></path><path d="M8 12v4"></path><path d="M16 12v4"></path><path d="M8 8v1"></path><path d="M16 8v1"></path></svg>
  },
  { 
    id: 'dark-web', 
    name: 'Dark Web Monitoring', 
    description: 'Monitor dark web for mentions of your organization and credentials',
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-eye"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path><circle cx="12" cy="12" r="3"></circle></svg>
  },
  { 
    id: 'infrastructure', 
    name: 'Infrastructure Scans', 
    description: 'Scan infrastructure for vulnerabilities and misconfigurations',
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-server"><rect width="20" height="8" x="2" y="2" rx="2" ry="2"></rect><rect width="20" height="8" x="2" y="14" rx="2" ry="2"></rect><line x1="6" x2="6.01" y1="6" y2="6"></line><line x1="6" x2="6.01" y1="18" y2="18"></line></svg>
  },
  { 
    id: 'web-vulnerabilities', 
    name: 'Web Vulnerabilities', 
    description: 'Scan web applications for security vulnerabilities',
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-globe"><circle cx="12" cy="12" r="10"></circle><line x1="2" x2="22" y1="12" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
  },
  { 
    id: 'api-vulnerabilities', 
    name: 'API Vulnerabilities', 
    description: 'Scan APIs for security vulnerabilities and misconfigurations',
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-webhook"><path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2"></path><path d="m6 17 3.13-5.78c.53-.97.43-2.22-.26-3.12a4 4 0 1 1 6.78-4.18"></path><path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8"></path></svg>
  },
  { 
    id: 'threat-intelligence', 
    name: 'Threat Intelligence', 
    description: 'Gather intelligence on threats targeting your organization',
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-radar"><circle cx="12" cy="12" r="10"></circle><path d="M12 12v-2"></path><path d="M12 12h4"></path><circle cx="12" cy="12" r="4"></circle></svg>
  },
  { 
    id: 'osint', 
    name: 'OSINT', 
    description: 'Open Source Intelligence gathering for your organization',
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-search"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>
  },
];

// Dummy data for scan statuses
const initialScanStatuses: ScanStatus[] = [
  {
    id: '1',
    scanType: 'Code Leaks',
    status: 'running',
    progress: 45,
    startTime: '2025-04-07T14:30:00Z',
  },
  {
    id: '2',
    scanType: 'Dark Web Monitoring',
    status: 'monitoring',
    progress: 100,
    startTime: '2025-04-06T10:15:00Z',
  },
  {
    id: '3',
    scanType: 'Infrastructure Scans',
    status: 'completed',
    progress: 100,
    startTime: '2025-04-05T08:20:00Z',
    endTime: '2025-04-05T09:45:00Z',
    findings: 12,
  },
  {
    id: '4',
    scanType: 'Web Vulnerabilities',
    status: 'failed',
    progress: 68,
    startTime: '2025-04-04T16:10:00Z',
    endTime: '2025-04-04T16:45:00Z',
  },
  {
    id: '5',
    scanType: 'API Vulnerabilities',
    status: 'scheduled',
    progress: 0,
    startTime: '2025-04-08T12:00:00Z',
  },
  {
    id: '6',
    scanType: 'Threat Intelligence',
    status: 'paused',
    progress: 72,
    startTime: '2025-04-03T11:30:00Z',
  },
  {
    id: '7',
    scanType: '3rd Party Risks',
    status: 'on-hold',
    progress: 30,
    startTime: '2025-04-02T09:45:00Z',
  },
];

// Helper function to get status badge variant
const getStatusBadgeVariant = (status: ScanStatus['status']) => {
  switch (status) {
    case 'running':
      return 'info';
    case 'monitoring':
      return 'active';
    case 'on-hold':
      return 'warning';
    case 'paused':
      return 'secondary';
    case 'scheduled':
      return 'default';
    case 'completed':
      return 'success';
    case 'failed':
      return 'destructive';
    default:
      return 'default';
  }
};

// Helper function to get status icon
const getStatusIcon = (status: ScanStatus['status']) => {
  switch (status) {
    case 'running':
      return <RefreshCw className="h-4 w-4 animate-spin" />;
    case 'monitoring':
      return <RefreshCw className="h-4 w-4" />;
    case 'on-hold':
      return <Clock className="h-4 w-4" />;
    case 'paused':
      return <Pause className="h-4 w-4" />;
    case 'scheduled':
      return <Calendar className="h-4 w-4" />;
    case 'completed':
      return <CheckCircle className="h-4 w-4" />;
    case 'failed':
      return <AlertTriangle className="h-4 w-4" />;
    default:
      return null;
  }
};

// Format date helper
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString();
};

const ScanSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('configure');
  const [scanStatuses, setScanStatuses] = useState<ScanStatus[]>(initialScanStatuses);
  const [selectedScanTypes, setSelectedScanTypes] = useState<string[]>([]);

  // Toggle scan type selection
  const toggleScanType = (scanTypeId: string) => {
    if (selectedScanTypes.includes(scanTypeId)) {
      setSelectedScanTypes(selectedScanTypes.filter(id => id !== scanTypeId));
    } else {
      setSelectedScanTypes([...selectedScanTypes, scanTypeId]);
    }
  };

  // Start a new scan
  const startScan = () => {
    if (selectedScanTypes.length === 0) return;
    
    const newScans = selectedScanTypes.map(scanTypeId => {
      const scanType = scanTypes.find(type => type.id === scanTypeId);
      return {
        id: `new-${Date.now()}-${scanTypeId}`,
        scanType: scanType?.name || '',
        status: 'running' as const,
        progress: 0,
        startTime: new Date().toISOString(),
      };
    });
    
    setScanStatuses([...newScans, ...scanStatuses]);
    setSelectedScanTypes([]);
    setActiveTab('status');
  };

  // Schedule a scan
  const scheduleScan = () => {
    if (selectedScanTypes.length === 0) return;
    
    const scheduledTime = new Date();
    scheduledTime.setHours(scheduledTime.getHours() + 24); // Schedule for tomorrow
    
    const newScans = selectedScanTypes.map(scanTypeId => {
      const scanType = scanTypes.find(type => type.id === scanTypeId);
      return {
        id: `scheduled-${Date.now()}-${scanTypeId}`,
        scanType: scanType?.name || '',
        status: 'scheduled' as const,
        progress: 0,
        startTime: scheduledTime.toISOString(),
      };
    });
    
    setScanStatuses([...newScans, ...scanStatuses]);
    setSelectedScanTypes([]);
    setActiveTab('status');
  };

  // Pause a scan
  const pauseScan = (scanId: string) => {
    setScanStatuses(
      scanStatuses.map(scan => 
        scan.id === scanId ? { ...scan, status: 'paused' } : scan
      )
    );
  };

  // Resume a scan
  const resumeScan = (scanId: string) => {
    setScanStatuses(
      scanStatuses.map(scan => 
        scan.id === scanId ? { ...scan, status: 'running' } : scan
      )
    );
  };

  // Cancel a scan
  const cancelScan = (scanId: string) => {
    setScanStatuses(scanStatuses.filter(scan => scan.id !== scanId));
  };

  return (
    <div className="container mx-auto py-6 text-white">
      <h1 className="text-2xl font-bold mb-6 text-white">Scan Settings and Status</h1>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#0F1629] border border-gray-800">
          <TabsTrigger value="configure">Configure Scans</TabsTrigger>
          <TabsTrigger value="status">Scan Status</TabsTrigger>
        </TabsList> */}
        
        <TabsContent value="configure" className="space-y-6">
          <Card className="bg-[#0F1629] border border-gray-800 shadow-md">
            <CardHeader>
              <CardTitle className="text-white">Select Scan Types</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                {scanTypes.map(scanType => (
                  <div 
                    key={scanType.id}
                    className={`border rounded-xl p-4 cursor-pointer transition-all ${
                      selectedScanTypes.includes(scanType.id) 
                        ? 'border-blue-500 bg-blue-500/10 text-blue-400' 
                        : 'border-gray-800 bg-[#1A1F2E]/80 text-gray-400 hover:border-gray-700 hover:bg-[#1A1F2E] hover:text-white'
                    }`}
                    onClick={() => toggleScanType(scanType.id)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className="mr-2 text-blue-400">
                          {scanType.icon}
                        </div>
                        <h3 className="font-medium">{scanType.name}</h3>
                      </div>
                      
                      {/* Show status badge if there's a recent scan for this type */}
                      {scanStatuses.find(s => s.scanType === scanType.name) && (
                        <Badge 
                          variant={getStatusBadgeVariant(scanStatuses.find(s => s.scanType === scanType.name)?.status || 'completed')} 
                          className="flex items-center"
                        >
                          {getStatusIcon(scanStatuses.find(s => s.scanType === scanType.name)?.status || 'completed')}
                          <span className="ml-1 text-white">
                            {(scanStatuses.find(s => s.scanType === scanType.name)?.status || 'completed').charAt(0).toUpperCase() + 
                             (scanStatuses.find(s => s.scanType === scanType.name)?.status || 'completed').slice(1)}
                          </span>
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-400 mb-2">{scanType.description}</p>
                    
                    {/* Show scan timing information if available */}
                    {scanStatuses.find(s => s.scanType === scanType.name) && (
                      <div className="text-xs text-gray-500 mt-2 space-y-1">
                        <div className="flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          <span>Started: {formatDate(scanStatuses.find(s => s.scanType === scanType.name)?.startTime || '')}</span>
                        </div>
                        {scanStatuses.find(s => s.scanType === scanType.name)?.endTime && (
                          <div className="flex items-center">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            <span>Completed: {formatDate(scanStatuses.find(s => s.scanType === scanType.name)?.endTime || '')}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          <div className="flex justify-end space-x-4">
            <Button
              className="bg-[#0F1629] border border-gray-800 hover:bg-[#1A1F2E] hover:text-white"
              variant="outline" 
              onClick={scheduleScan}
              disabled={selectedScanTypes.length === 0}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Schedule Scan
            </Button>
            <Button 
              className="bg-[#0F1629] border border-gray-800 hover:bg-[#1A1F2E] hover:text-white"
              onClick={startScan}
              disabled={selectedScanTypes.length === 0}
            >
              <Play className="mr-2 h-4 w-4" />
              Start Scan Now
            </Button>
          </div>
        </TabsContent>
        
        <TabsContent value="status" className="space-y-4">
          <Card className="bg-[#0F1629] border border-gray-800 shadow-md">
            <CardHeader>
              <CardTitle className="text-white">Scan Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {scanStatuses.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    No scans have been configured yet
                  </div>
                ) : (
                  scanStatuses.map(scan => (
                    <div key={scan.id} className="border border-gray-800 bg-[#1A1F2E]/50 rounded-lg p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                        <div className="flex items-center mb-2 md:mb-0">
                          <h3 className="font-medium mr-3 text-white">{scan.scanType}</h3>
                          <Badge variant={getStatusBadgeVariant(scan.status)} className="flex items-center">
                            {getStatusIcon(scan.status)}
                            <span className="ml-1 text-white">{scan.status.charAt(0).toUpperCase() + scan.status.slice(1)}</span>
                          </Badge>
                        </div>
                        <div className="flex space-x-2">
                          {scan.status === 'running' && (
                            <Button size="sm" variant="outline" onClick={() => pauseScan(scan.id)}>
                              <Pause className="h-4 w-4" />
                            </Button>
                          )}
                          {scan.status === 'paused' && (
                            <Button size="sm" variant="outline" onClick={() => resumeScan(scan.id)}>
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                          {['running', 'paused', 'on-hold', 'scheduled'].includes(scan.status) && (
                            <Button size="sm" variant="destructive" onClick={() => cancelScan(scan.id)}>
                              Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Progress</span>
                          <span className="text-white">{scan.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-800/50 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full text-white ${
                              scan.status === 'failed' 
                                ? 'bg-red-500' 
                                : scan.status === 'completed' || scan.status === 'monitoring'
                                  ? 'bg-green-500'
                                  : 'bg-blue-500'
                            }`}
                            style={{ width: `${scan.progress}%` }}
                          ></div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm mt-2">
                          <div>
                            <span className="text-gray-400 mr-2">Started:</span>
                            <span className="text-white">{formatDate(scan.startTime)}</span>
                          </div>
                          {scan.endTime && (
                            <div>
                              <span className="text-gray-400 mr-2">Completed:</span>
                              <span className="text-white">{formatDate(scan.endTime)}</span>
                            </div>
                          )}
                          {scan.findings !== undefined && (
                            <div>
                              <span className="text-gray-400 mr-2">Findings:</span>
                              <span className="text-white">{scan.findings}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ScanSettings;
